export const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    slug: "privacy",
    body: `DNA Finance ("we", "us", "our") is committed to protecting your privacy. This policy explains how we collect, use, and protect your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.

**Data Controller:** DNA Finance, company number 16322517
**Registered office:** 556 Mumbles Road, Mumbles, Swansea, SA3 4DL
**Contact:** Admin@dnafinance.co.uk | 01792 677652
**ICO registration number:** ZC234972

## 1. What Information We Collect

We may collect the following information when you use our website, apply for finance, or contact us:

- Name, contact details (email, phone, address)
- Business details (company name, trading history, financial information relevant to your application)
- Information about the finance or equipment/asset you are seeking to fund
- Technical information such as IP address and browsing behaviour (see our Cookie Policy)
- Any other information you choose to provide when contacting us

## 2. How We Use Your Information

We use your information to:

- Assess your finance requirements and introduce you to suitable lenders on our panel
- Communicate with you about your enquiry or application
- Carry out anti-money laundering (AML) and identity verification checks as required by law
- Improve our website and services
- Comply with our legal and regulatory obligations, including those set by the FCA
- Send you relevant updates, where you have consented to this

## 3. Our Legal Basis for Processing

We process your data based on:

- **Contract** — to take steps to provide services you have requested
- **Legal obligation** — to meet AML, KYC, and FCA regulatory requirements
- **Legitimate interests** — to operate and improve our business, subject to your rights
- **Consent** — where you have opted in to marketing communications, which you may withdraw at any time

## 4. Who We Share Your Information With

To arrange finance on your behalf, we may share relevant information with:

- Lenders on our panel, to assess and process your application
- Credit reference and fraud prevention agencies
- Regulatory bodies, where required by law (e.g. the FCA)
- IT and service providers who support our business operations, under appropriate data protection agreements

We do not sell your personal data to third parties.

## 5. How Long We Keep Your Data

We retain personal data only as long as necessary to fulfil the purposes described in this policy, or as required by law and FCA regulatory requirements (which may require certain records to be kept for a minimum period after a relationship ends).

## 6. Your Rights

Under UK GDPR, you have the right to:

- Access the personal data we hold about you
- Request correction of inaccurate data
- Request deletion of your data, where applicable
- Restrict or object to certain processing
- Request data portability
- Withdraw consent at any time, where processing is based on consent

To exercise any of these rights, contact us at Admin@dnafinance.co.uk.

You also have the right to lodge a complaint with the **Information Commissioner's Office (ICO)** if you believe your data has been mishandled:

Website: [ico.org.uk](https://ico.org.uk)
Phone: 0303 123 1113

## 7. Security

We take appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, or misuse.

## 8. Changes to This Policy

We may update this policy from time to time. The current version will always be available on this page.

---
*Last updated: 02.03.2026*`,
  },
  terms: {
    title: "Terms and Conditions",
    slug: "terms",
    body: `These terms and conditions ("Terms") govern your use of the DNA Finance website (www.dnafinance.co.uk) and any services provided by DNA Finance ("we", "us", "our"). By using our website or services, you agree to these Terms.

**Company:** DNA Finance
**Company number:** 16322517
**Registered office:** 556 Mumbles Road, Mumbles, Swansea, SA3 4DL
**Contact:** Admin@dnafinance.co.uk | 01792 677652

## 1. Who We Are

DNA Finance is a commercial finance broker. We introduce and arrange business finance — including asset, equipment, and commercial loan products — between our clients and a panel of lenders. **We are a credit broker, not a lender.** We do not provide funding directly.

The commercial finance products we arrange are provided to business customers and fall outside the scope of FCA-regulated consumer credit activity. If your circumstances mean a product you're seeking may be regulated, please tell us so we can advise accordingly.

## 1a. Eligibility

Our services are available exclusively to **limited companies** that have been trading for a **minimum of 2 years**. We do not arrange finance for sole traders, partnerships, or individuals. This eligibility criteria reflects the scope of products we are set up to arrange and helps ensure the finance we introduce falls outside regulated consumer credit activity. We reserve the right to decline any enquiry that falls outside this criteria.

## 2. Our Services

- We will assess your finance requirements and, where appropriate, introduce you to one or more lenders from our panel who may be able to assist.
- We do not guarantee that any lender will offer you finance, or that terms offered will match your expectations.
- We may receive commission or a fee from a lender when a finance agreement completes as a result of our introduction. This does not increase the amount you pay.
- Where we charge a fee directly to you, this will be disclosed and agreed with you in writing before you proceed.

## 3. Your Responsibilities

- You agree to provide accurate, complete, and up-to-date information when applying for finance through us.
- You are responsible for reviewing and understanding the terms of any finance agreement before signing it. We recommend seeking independent financial or legal advice if you are unsure.
- Any finance agreement you enter into is directly between you and the lender; DNA Finance is not a party to that agreement.

## 4. No Advice

Unless explicitly agreed in writing, DNA Finance provides an introduction and brokerage service only. We do not provide regulated financial advice on the suitability of specific products for your circumstances unless stated otherwise.

## 5. Website Use

- Content on this website is for general information only and does not constitute financial advice.
- We take reasonable care to ensure information on this website is accurate but do not guarantee it is complete, current, or error-free.
- You must not misuse this website (e.g. introducing viruses, attempting unauthorised access, or submitting false information).

## 6. Intellectual Property

All content on this website, including text, logos, and design, is owned by or licensed to DNA Finance and may not be reproduced without our permission.

## 7. Limitation of Liability

To the extent permitted by law, DNA Finance is not liable for any indirect or consequential loss arising from your use of this website or our services. Nothing in these Terms limits our liability for fraud, or for death or personal injury caused by negligence, or any liability that cannot be excluded by law.

## 8. Complaints

If you are unhappy with our service, please see our Complaints Procedure for how to raise this with us and how we handle it.

## 9. Changes to These Terms

We may update these Terms from time to time. The current version will always be available on this page.

## 10. Governing Law

These Terms are governed by the laws of England and Wales, and any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.

---
*Last updated: 02.03.2026*`,
  },
  complaints: {
    title: "Complaints Procedure",
    slug: "complaints",
    body: `DNA Finance (company number 16322517) is committed to providing a high standard of service. If something has gone wrong, we want to hear about it so we can put it right.

**Please note:** DNA Finance arranges commercial finance products that fall outside FCA-regulated activity. This complaints procedure sets out our internal process for handling any concerns you raise with us.

## How to Make a Complaint

You can contact us in any of the following ways:

- **Email:** Admin@dnafinance.co.uk
- **Phone:** 01792 677652
- **Post:** DNA Finance, 556 Mumbles Road, Mumbles, Swansea, SA3 4DL

Please provide as much detail as possible, including your name, contact details, any reference or application number, and a clear description of your complaint.

## What Happens Next

**Acknowledgement:** We will acknowledge your complaint promptly, normally within 3 business days of receiving it.

**Investigation:** A member of our team who was not directly involved in the matter will look into your complaint thoroughly and fairly.

**Resolution:** We aim to resolve most complaints within 14 days. Where a complaint is more complex, we will keep you updated on progress and aim to provide a full response within 28 days.

Our response will either:
- Uphold your complaint and explain what we will do to put things right, or
- Explain why we do not uphold your complaint, or
- Let you know if we need more time to investigate.

## If You Remain Unsatisfied

If you are not happy with the outcome, you are welcome to request that your complaint be reviewed by a senior member of the team. We are committed to working with you to reach a fair resolution.

Where a complaint relates to a specific lender or finance provider on our panel, we will direct you to that lender's own complaints process where relevant, as the finance agreement itself is between you and the lender.

---
*This policy is reviewed periodically.*
*Last updated: 02.03.2026*`,
  },
  cookies: {
    title: "Cookie Policy",
    slug: "cookies",
    body: `This Cookie Policy explains how DNA Finance ("we", "us", "our") uses cookies and similar technologies on www.dnafinance.co.uk.

## 1. What Are Cookies

Cookies are small text files placed on your device when you visit a website. They help websites function properly, remember your preferences, and provide information to site owners about how the website is used.

## 2. Types of Cookies We Use

**Essential Cookies**
These are necessary for the website to function correctly (e.g. enabling forms, remembering your session). The website cannot function properly without these, and they do not require consent.

**Analytics Cookies**
We may use tools such as Google Analytics to understand how visitors use our website — which pages are viewed, how long visitors stay, and how they found us. This helps us improve the site. These cookies collect anonymised or aggregated data where possible.

**Marketing / Advertising Cookies**
We may use cookies from platforms such as Google Ads or Meta (Facebook/Instagram) to measure the effectiveness of our advertising campaigns and to show relevant ads to visitors who have shown interest in our services. These cookies may track your activity across other websites.

**Functionality Cookies**
These remember choices you make on our site (such as form inputs) to provide a more personalised experience.

## 3. Managing Your Cookie Preferences

When you first visit our website, you will be shown a cookie consent banner allowing you to accept or manage non-essential cookies.

You can also control or delete cookies through your browser settings at any time. Please note that blocking essential cookies may affect the functionality of our website. Most browsers allow you to:

- View what cookies are stored and delete them individually
- Block third-party cookies
- Block all cookies from specific sites
- Block all cookies from being set
- Delete all cookies when you close your browser

## 4. Third-Party Cookies

Some cookies are set by third-party services that appear on our pages (e.g. analytics or advertising providers). We do not control these cookies — please refer to the relevant third party's own cookie/privacy policy for more information:

- Google: [policies.google.com/technologies/cookies](https://policies.google.com/technologies/cookies)
- Meta: [www.facebook.com/policies/cookies](https://www.facebook.com/policies/cookies)

## 5. Changes to This Policy

We may update this Cookie Policy from time to time to reflect changes in the cookies we use or for legal/regulatory reasons. The current version will always be available on this page.

## 6. Contact Us

If you have questions about this Cookie Policy, contact us at:

Admin@dnafinance.co.uk | 01792 677652

---
*Last updated: 02.03.2026*`,
  },
};
